/**
 * f16.1: the scoped landing dashboard, after `dashboard.html`.
 *
 * One query, one endpoint. The browser decides nothing: `access`,
 * `capabilities` and every `null` arrive from the server, and a `null` is
 * rendered as absence -- never as `0` (BR-F16.1-1/2). A caller named nowhere
 * gets f9.2's "No access yet" state and no card at all.
 *
 * Refetched on window focus with a 60 s stale time; the review screen owns
 * the live push (f12.2). The key carries the server-resolved scope, so a
 * session reload that grants an assignment shows on the next render.
 */

import { useMemo } from 'react';
import { ArrowRight, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api, type DashboardScoped } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { useSessionScope } from '@/session/SessionScopeContext';
import { NoAccessYet } from '@/shell/AccessStates';
import '@/styles/review.css';
import '@/styles/dashboard.css';

import { BusinessUnitsTable } from './BusinessUnitsTable';
import { CycleCard } from './CycleCard';
import { AttentionCard, PreparationCard } from './SideCards';
import { StatCards } from './StatCards';
import { cycleActionPath } from './dashboardView';

export const DASHBOARD_STALE_MS = 60_000;

export function useDashboard() {
  const { session } = useSessionScope();
  const scopeKey = useMemo(
    () => JSON.stringify([session?.user?.id ?? null, session?.navigation ?? [], session?.scope ?? null]),
    [session],
  );
  return useQuery({
    queryKey: queryKeys.dashboard(scopeKey),
    queryFn: () => api.getDashboard(),
    staleTime: DASHBOARD_STALE_MS,
    refetchOnWindowFocus: true,
  });
}

function PageHead({ body }: { body: DashboardScoped | null }) {
  const { canSee } = useSessionScope();
  const year = body?.cycle?.period_year;
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Dashboard</h1>
        <div className="page-sub">
          {`GFT US delivery and commitment overview${year ? ` · fiscal year ${year}` : ''}`}
        </div>
      </div>
      {body && (
        <div className="page-actions">
          {canSee('data-import') && (
            <Link className="btn btn-secondary" to="/data-import">
              <Upload aria-hidden="true" /> Import master
            </Link>
          )}
          {body.cycle && (
            <Link className="btn btn-primary" to={cycleActionPath(body)}>
              <ArrowRight aria-hidden="true" /> {`Open cycle ${body.cycle.code}`}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading the dashboard">
      <div className="grid grid-4">
        {[1, 2, 3, 4].map((bar) => (
          <div key={bar} className={`card stat-card rv-bar-${bar}`}>
            <div className="skeleton-bar dash-skeleton-label" />
            <div className="skeleton-bar dash-skeleton-value" />
          </div>
        ))}
      </div>
      <div className="split-2-1 mt-6">
        <div className="card dash-skeleton-card" />
        <div className="card dash-skeleton-card" />
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { canSee } = useSessionScope();
  const query = useDashboard();

  if (query.isPending) {
    return (
      <>
        <PageHead body={null} />
        <Skeleton />
      </>
    );
  }

  if (query.isError) {
    return (
      <>
        <PageHead body={null} />
        <div className="card">
          <div className="card-body dash-error">
            <p>{query.error instanceof Error ? query.error.message : 'The dashboard could not be loaded.'}</p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void query.refetch()}>
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  const body = query.data;
  if (body.access === 'NO_ACCESS') return <NoAccessYet />;

  return (
    <>
      <PageHead body={body} />
      <StatCards body={body} canImport={canSee('data-import')} />
      <div className="split-2-1 mt-6">
        <CycleCard body={body} />
        <div className="stack">
          <PreparationCard body={body} />
          <AttentionCard body={body} />
        </div>
      </div>
      <BusinessUnitsTable body={body} canViewUnits={canSee('business-units')} />
    </>
  );
}
